import {HelpCircle} from "lucide-react";
import type {ReactNode} from "react";

import {Button} from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "@/components/ui/popover";

import {LearnMoreLink} from "./learn-more-link";
import {useOptionalHelpScreenContext} from "./help-screen-provider";

type FieldHelpProps = {
  helpKey: string;
  /** When set, shows when_on / when_off from API instead of summary alone. */
  watchValue?: boolean;
  className?: string;
};

export function FieldHelp({helpKey, watchValue, className}: FieldHelpProps) {
  const context = useOptionalHelpScreenContext();
  const field = context?.fieldsByKey.get(helpKey);

  if (!field) {
    return null;
  }

  const body =
    watchValue !== undefined
      ? watchValue
        ? (field.when_on ?? field.summary)
        : (field.when_off ?? field.summary)
      : field.summary;

  return (
    <Popover>
        <PopoverTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-7 shrink-0 text-muted-foreground hover:text-foreground"
              aria-label={`Help for ${field.label}`}
            >
              <HelpCircle className="size-3.5" />
            </Button>
          }
        />
        <PopoverContent className="w-80 max-w-[min(20rem,calc(100vw-2rem))] gap-3 p-4">
          <PopoverHeader>
            <PopoverTitle>{field.label}</PopoverTitle>
            <PopoverDescription className="text-sm leading-relaxed text-muted-foreground">
              {body}
            </PopoverDescription>
          </PopoverHeader>
          {field.learn_more_section && context?.domain ? (
            <LearnMoreLink
              domain={context.domain}
              sectionId={field.learn_more_section}
              label="Learn more"
            />
          ) : null}
        </PopoverContent>
      </Popover>
  );
}

export function FieldHelpLabel({
  helpKey,
  children,
  watchValue,
  className,
  showIcon = true,
}: {
  helpKey: string;
  children: ReactNode;
  watchValue?: boolean;
  className?: string;
  /** When false, only sets `data-help-target` for guided tours (no ? popover). */
  showIcon?: boolean;
}) {
  return (
    <div className={`flex items-center gap-1 ${className ?? ""}`} data-help-target={helpKey}>
      {children}
      {showIcon ? <FieldHelp helpKey={helpKey} watchValue={watchValue} /> : null}
    </div>
  );
}
